import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import TicketService from './ticket.service';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';

type ActionType = 'updateTicketNotes';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useTicketMutationsNotes = (): {
  updateTicketNotes: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  isUpdatingTicketNotes: Record<string, boolean>;
  setIsUpdatingTicketNotes: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingTicketNotes, setIsUpdatingTicketNotes] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const ticketService = TicketService();
    const action: { [key in ActionType]?: Function } = {
      updateTicketNotes: ticketService.postNotes,
      // addNote: ticketService.postNewNote
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const { mutateAsync: updateTicketNotes } = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: async ({ actionType, ...newNote }) => {
      setIsUpdatingTicketNotes({ ...isUpdatingTicketNotes, [actionType]: true });

      const preparedData = {
        //todo Add the username of the person currently signed in
        "Message": newNote.data.message,
        "Createtime": dayjs(),
        "AuthorName": newNote.data.authorname,
      }
      // Cancel any outgoing refetches 
      await queryClient.cancelQueries(['notes-by-id', newNote.data.ticketid]);

      // Get current notes
      const previousNotes = queryClient.getQueryData(['notes-by-id', newNote.data.ticketid]) || [];

      // Create optimistic note with required fields
      const optimisticNote = {
        Nid: Math.random(), // Temporary ID
        Message: newNote.data.message,
        Createtime: dayjs().toISOString(),
        AuthorName: newNote.data.authorname,
        TimeZone: '',
        TimeZoneOffset: 0
      };

      // Update cache with optimistic note
      queryClient.setQueryData(
        ['notes-by-id', newNote.data.ticketid],
        [...previousNotes, optimisticNote]
      );

      // Return context for rollback
      return { previousNotes };
      // queryClient.setQueryData(['notes-by-id', newNote.data.ticketid], (old: any) => [...old, preparedData])
    },
    onSuccess: (data: any, variables: any) => {
      // console.log('notes onSuccess:', data, variables);
      // console.log('Mutation successful');
      // console.log('note variables.data:', variables.data);
      // console.log('data.data.Success:', data.data.Success);
      // queryClient.invalidateQueries({ queryKey: ['all-tickets'] });
      if (typeof (data.data.Nid) === 'number' && variables.actionType === 'updateTicketNotes') {
        // console.log('successful update');
        enqueueSnackbar('Note saved successfully', { variant: 'success' });
      } else if (typeof (data.data.Nid) !== 'number' && data.data.Success === false) {
        enqueueSnackbar('Note update failed', { variant: 'error' });
      }
      // if (variables.actionType === 'deleteTicket' && data.ticketId) {
      //     queryClient.removeQueries({ queryKey: ['ticket', data.ticketId] })
      //     enqueueSnackbar(`Ticket #${data.ticketId} deleted successfully`, { variant: 'success' })
      //     queryClient.invalidateQueries({ queryKey: ['archived-tickets'], exact: true });
      // }
    },
    onError: (error: any, variables: any) => {
      // console.error('Error during mutation:', error);
      // console.warn('ERROR', variables);
      if (Object.keys(variables.data).length > 2 && variables.actionType === 'updateTicketNotes') {
        enqueueSnackbar('Note Field updates failed', { variant: 'error' });
      } else if (variables.data.TId && variables.actionType === 'updateTicketNotes') {
        enqueueSnackbar('Note Field update failed', { variant: 'error' });
      }

      if (variables.actionType === 'deleteTicket' && variables.data.TId) {
        enqueueSnackbar(`Ticket #${variables.data[0].TicketID} delete failed`, {
          variant: 'error',
        });
      }
    },
    // onSettled: ({ actionType }) => {
    //   setIsUpdating({ ...isUpdating, [actionType]: false });
    // },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes-by-id'] });
    },
  });

  return { updateTicketNotes, isUpdatingTicketNotes, setIsUpdatingTicketNotes };
};

export default useTicketMutationsNotes;
